// Denominaciones (billetes/monedas) para el arqueo de caja. POS → Configuración → Denominaciones.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins, Landmark, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Denominacion, Modal, Switch, TIPO_DENOMINACION, fieldCls, pos, posRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

const money = (v: number) => Number(v).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const vacio = { valor: 0, tipo: 'billete', orden: 0, es_activo: true };

export function Denominaciones() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Denominacion[]>({ queryKey: ['pos-denominaciones'], queryFn: () => pos.list<Denominacion>('denominaciones', '?select=*&order=orden,valor') });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (d?: Denominacion) => {
    if (d) { setEditId(d.id); setForm({ valor: d.valor, tipo: d.tipo, orden: d.orden, es_activo: d.es_activo }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => {
      const body = { valor: Number(form.valor) || 0, tipo: form.tipo, orden: Number(form.orden) || 0, es_activo: form.es_activo };
      return editId ? pos.update('denominaciones', editId, body) : pos.create('denominaciones', body);
    },
    onSuccess: () => { toast.success('Denominación guardada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['pos-denominaciones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => pos.remove('denominaciones', id),
    onSuccess: () => { toast.success('Denominación eliminada'); void qc.invalidateQueries({ queryKey: ['pos-denominaciones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  // Juego estándar de billetes/monedas según el país de la empresa (EC/PE/CO);
  // idempotente: no pisa las denominaciones ya existentes.
  const cargarEstandar = useMutation({
    mutationFn: () => posRpc<{ pais: string; moneda: string; insertadas: number }>('denominaciones_estandar_cargar', {}),
    onSuccess: (r) => {
      toast.success(r.insertadas > 0
        ? `${r.insertadas} denominación(es) estándar de ${r.moneda} agregadas`
        : `Las denominaciones estándar de ${r.moneda} ya estaban completas`);
      void qc.invalidateQueries({ queryKey: ['pos-denominaciones'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Coins className="w-5 h-5" /> Denominaciones</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" isLoading={cargarEstandar.isPending}
                  title="Carga el juego estándar de billetes y monedas del país de la empresa (no pisa las existentes)"
                  onClick={() => cargarEstandar.mutate()}>
            <Landmark className="w-4 h-4 mr-1" /> Cargar estándar del país
          </Button>
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Denominaciones ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Orden</th><th className="py-2 pr-3 text-right">Valor</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin denominaciones</td></tr>}
              {rows.map((d) => (
                <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{d.orden}</td>
                  <td className="py-2 pr-3 text-right font-medium">{money(d.valor)}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs capitalize">{d.tipo}</span></td>
                  <td className="py-2 pr-3">{d.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(d)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar ${money(d.valor)}?`, { peligro: true })) eliminar.mutate(d.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar denominación' : 'Nueva denominación'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Valor *"><input type="number" step="0.01" min="0" className={fieldCls} value={form.valor} onChange={(e) => set({ valor: Number(e.target.value) })} /></Campo>
              <Campo label="Tipo">
                <select className={fieldCls} value={form.tipo} onChange={(e) => set({ tipo: e.target.value })}>
                  {TIPO_DENOMINACION.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
                </select>
              </Campo>
            </div>
            <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => set({ orden: Number(e.target.value) })} /></Campo>
            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activa" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.valor} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
